<template>
  <Teleport to="body">
    <div
      ref="anchorEl"
      class="foil-pick-anchor"
      :style="anchorStyle"
      @pointerdown.stop
      @contextmenu.prevent.stop
    >
      <label class="visually-hidden" :for="selectId">{{ label }}</label>
      <select
        :id="selectId"
        ref="selectEl"
        class="foil-pick-select"
        :value="modelValue"
        :aria-label="label"
        @change="onChange"
        @keydown.escape.stop.prevent="emit('close')"
      >
        <option v-for="id in choices" :key="id" :value="id">
          {{ t(`packs.rarity.${id}`) }}
        </option>
      </select>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { asCardFoil, type CardFoilId } from '@shared/index'
import { useI18n } from '../i18n/t'

export interface FoilPickRect {
  left: number
  top: number
  width: number
  height: number
}

const SELECT_WIDTH = 200
const SELECT_HEIGHT = 40
const VIEWPORT_PAD = 8
let foilPickSeq = 0

const props = defineProps<{
  rect: FoilPickRect
  choices: CardFoilId[]
  modelValue: CardFoilId
  label: string
}>()

const emit = defineEmits<{
  close: []
  'update:modelValue': [foil: CardFoilId]
}>()

const { t } = useI18n()
const anchorEl = ref<HTMLElement | null>(null)
const selectEl = ref<HTMLSelectElement | null>(null)
const selectId = `foil-pick-${++foilPickSeq}`

const anchorStyle = computed(() => {
  const rect = props.rect
  const maxW = Math.max(120, window.innerWidth - VIEWPORT_PAD * 2)
  const width = Math.min(SELECT_WIDTH, maxW)
  const left = clamp(
    rect.left + rect.width / 2 - width / 2,
    VIEWPORT_PAD,
    window.innerWidth - width - VIEWPORT_PAD,
  )
  const top = clamp(
    rect.top + rect.height / 2 - SELECT_HEIGHT / 2,
    VIEWPORT_PAD,
    window.innerHeight - SELECT_HEIGHT - VIEWPORT_PAD,
  )
  return {
    left: `${left}px`,
    top: `${top}px`,
    width: `${width}px`,
  }
})

/**
 * Keep the floating select inside the viewport.
 */
function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

function onChange(event: Event): void {
  const value = (event.target as HTMLSelectElement).value
  emit('update:modelValue', asCardFoil(value))
  emit('close')
}

function onDismiss(): void {
  emit('close')
}

function onDocPointerDown(event: PointerEvent): void {
  const root = anchorEl.value
  if (root && root.contains(event.target as Node)) return
  emit('close')
}

onMounted(() => {
  window.addEventListener('scroll', onDismiss, true)
  window.addEventListener('resize', onDismiss)
  document.addEventListener('pointerdown', onDocPointerDown)
  selectEl.value?.focus()
})

onUnmounted(() => {
  window.removeEventListener('scroll', onDismiss, true)
  window.removeEventListener('resize', onDismiss)
  document.removeEventListener('pointerdown', onDocPointerDown)
})
</script>

<style scoped>
.foil-pick-anchor {
  position: fixed;
  z-index: 70;
  line-height: 0;
}

.foil-pick-select {
  display: block;
  box-sizing: border-box;
  width: 100%;
  min-height: 40px;
  padding: 8px 12px;
  border: 0;
  border-bottom: 2px solid var(--md-sys-color-primary, #e8c547);
  border-radius: 4px 4px 0 0;
  background: var(--md-sys-color-surface-container-high, #2a2a2e);
  color: var(--md-sys-color-on-surface, #e5e1e6);
  font-family: Roboto, system-ui, sans-serif;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
}

.foil-pick-select:focus {
  outline: none;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
